import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpLink, loggerLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { useState } from "react";

import { type AppRouter } from "@crew-api/server";
import { getBaseUrl, transformer } from "./utils";
import { supabase } from "@/lib/supabase";

export const trpc = createTRPCReact<AppRouter>({
  overrides: {
    useMutation: {
      async onSuccess(opts) {
        await opts.originalFn();
        await opts.queryClient.invalidateQueries();
      }
    }
  }
});

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  // Configure QueryClient with proper retry logic to prevent infinite loops
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        // Don't retry on authentication errors to prevent infinite loops
        retry: (failureCount, error) => {
          // Prevent retrying for authentication errors
          if ((error instanceof Error &&
            error.message.includes('You must be logged in')) ||
            error.message?.includes('UNAUTHORIZED')) {
            return false;
          }
          // Retry other errors up to 3 times
          return failureCount < 3;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      },
    },
  }));

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        loggerLink({
          // 'css' (default) emits %c browser escape codes that RN's console
          // can't render, so the logs come out as walls of CSS strings.
          // 'ansi' uses terminal colour codes that Metro renders cleanly.
          colorMode: 'ansi',
          enabled: (op) =>
            process.env.NODE_ENV === "development" ||
            (op.direction === "down" && op.result instanceof Error),
        }),
        httpLink({
          transformer,
          url: getBaseUrl() + "/api/trpc",
          async headers() {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return { Authorization: [] };
            return { Authorization: `Bearer ${session.access_token}` };
          },
        }),
      ],
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </trpc.Provider>
    </QueryClientProvider>
  );
}